import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { RJ } from '../../rapport/rapportTokens';

// §5 (ticket #37) — sticky mobile CTA for the finished course hub. Below 992px
// the desktop sticky right column (HubAside) collapses, so the primary action
// ("Voir le rapport de visite") gets a full-width fixed bar instead. Mirrors
// the DevisRecapLanding MobileCtaBar pattern: room reserved on the right for
// the Crisp launcher (76px) + the iOS safe area at the bottom.
const Bar = styled.div`
  display: none;

  @media (max-width: 991px) {
    display: block;
    position: fixed;
    left: 0;
    right: 0;
    bottom: 0;
    background: ${RJ.white};
    border-top: 1px solid ${RJ.border};
    box-shadow: 0 -4px 16px rgba(49, 73, 89, 0.08);
    padding: 16px 24px;
    /* Reserve the Crisp launcher footprint (76px) on the right. */
    padding-right: 76px;
    padding-bottom: calc(16px + env(safe-area-inset-bottom));
    z-index: 1000;
  }
`;

// Keeps the last block from hiding behind the fixed bar on mobile.
const Spacer = styled.div`
  display: none;

  @media (max-width: 991px) {
    display: block;
    height: calc(88px + env(safe-area-inset-bottom));
  }
`;

// Dedicated full-width green CTA anchor (ticket #41). The shared Button atom
// can't be reused here: rendered `as="a"` it loses its filled background and the
// global `a { color: #d5ff4f }` reset paints it as green-on-transparent (a link,
// not a pill). Same call as the DevisRecapLanding `Cta`: explicit brand charter
// (green #d5ff4f / text #314959 / radius 30px), full width, nowrap.
const CtaButton = styled.a`
  display: block;
  width: 100%;
  box-sizing: border-box;
  padding: 16px 20px;
  border-radius: ${RJ.radiusButton};
  background: ${RJ.green};
  color: ${RJ.dark};
  font-weight: 700;
  font-size: 16px;
  text-align: center;
  text-decoration: none;
  white-space: nowrap;

  &:hover {
    opacity: 0.85;
    color: ${RJ.dark};
  }
`;

const CourseMobileCtaBar = ({ course }) => {
  const { t } = useTranslation();
  if (!course || course.state !== 'finished') return null;
  const report = course.visit_report;
  if (!report || !report.published_at || !report.token) return null;

  return (
    <>
      <Spacer aria-hidden="true" />
      <Bar data-testid="course-mobile-cta-bar">
        <CtaButton
          href={`/rapport/${encodeURIComponent(report.token)}`}
          data-testid="course-mobile-cta">
          {t('sideactions.rapport.cta.view')}
        </CtaButton>
      </Bar>
    </>
  );
};

CourseMobileCtaBar.propTypes = {
  course: PropTypes.shape({
    state: PropTypes.string,
    visit_report: PropTypes.shape({
      published_at: PropTypes.string,
      token: PropTypes.string,
    }),
  }),
};

CourseMobileCtaBar.defaultProps = {
  course: null,
};

export default CourseMobileCtaBar;
